import { defineStore } from 'pinia'
import { ref } from 'vue'
import * as Sentry from '@sentry/vue'
import api from '../lib/axios'
import { useWorkspaceStore } from './workspace'
import { useTenancyStore } from './tenancy'
import { usePermissionsStore } from './permissions'
import { useBookmarksStore } from './bookmarks'
import { useRecentPagesStore } from './recentPages'

function setSentryUser(user) {
    if (user) {
        Sentry.setUser({ id: String(user.id), email: user.email, username: user.name })
    } else {
        Sentry.setUser(null)
    }
}

export const useAuthStore = defineStore('auth', () => {
    const user = ref(null)
    const ready = ref(false)
    // What the server accepts (App\Support\UploadLimits), for a check before uploading. The
    // defaults are the server's own, for the moment before bootstrap answers.
    const uploadLimits = ref({ file_max_kb: 8192, video_max_kb: 61440 })
    // Whether the viewer reads the lists in Stuart's names (Execute, Deep, Chase, Decide) wherever
    // they appear outside Home: a card window, an email's Add to list, a meeting's minutes.
    // CommitmentCards::isManager, sent once by bootstrap. A label, never a permission.
    const commitmentsManager = ref(false)

    let _bootstrapPromise = null

    async function fetchUser() {
        if (_bootstrapPromise) return _bootstrapPromise
        _bootstrapPromise = _doBootstrap().finally(() => { _bootstrapPromise = null })
        return _bootstrapPromise
    }

    async function _doBootstrap() {
        try {
            const { data } = await api.get('/me/bootstrap')
            const payload = data.data

            user.value = payload.user
            setSentryUser(payload.user)
            if (payload.upload_limits) uploadLimits.value = payload.upload_limits
            commitmentsManager.value = !!payload.commitments?.manager

            const ws = useWorkspaceStore()
            ws.hydrateFromModuleStates(payload.user.module_states ?? [])

            const tenancy = useTenancyStore()
            tenancy.hydrateFromBootstrap(payload.companies ?? [], payload.modules ?? [])

            // Loaded here because it is keyed to the active company, which bootstrap has
            // just settled. Not awaited: the sidebar shows everything until it arrives, and
            // waiting on it would put a permission call in front of every page load for a
            // list that hides nothing while enforcement is off.
            usePermissionsStore().load()
        } catch {
            user.value = null
            setSentryUser(null)
        } finally {
            ready.value = true
        }
    }

    async function sendOtp(email) {
        await api.get('/sanctum/csrf-cookie', { baseURL: '' })
        return api.post('/auth/send-otp', { email })
    }

    async function verifyOtp(email, code) {
        const { data } = await api.post('/auth/verify-otp', { email, code })
        user.value = data.data
        setSentryUser(data.data)
        return data
    }

    async function logout() {
        await api.post('/auth/logout')
        // Logout navigates without a reload, so the next person in this tab would otherwise
        // inherit this person's bookmarks and history.
        useBookmarksStore().reset()
        useRecentPagesStore().clear()
        user.value = null
        commitmentsManager.value = false
        setSentryUser(null)
    }

    const isAuthenticated = () => user.value !== null

    return { user, ready, uploadLimits, commitmentsManager, fetchUser, sendOtp, verifyOtp, logout, isAuthenticated }
})
